/**
 * Quota banner — surfaces an at-a-glance quota state (warning / exceeded /
 * cap_reached) across the app shell. Mirrors <LifecycleBanner>: reads the
 * compact summary on `GET /api/v1/me/billing-state` (already polled by the
 * lifecycle banner — a cheap all-role indicator; the full per-metric detail
 * lives on the owner/admin-only Usage page).
 *
 * Per the #328 contract (#185.7):
 * - Render only when `data?.quota && data.quota.banner.show &&
 *   data.quota.mode === "enforce"`. The backend already returns `quota: null`
 *   outside enforce, so the `mode` check is defensive belt-and-braces.
 * - First-paint / error / no-data: render nothing (don't flash, don't block).
 * - CTA "Upgrade" → /app/settings, visible to all roles, enabled only for
 *   owner/admin (matches LifecycleBanner's gating).
 * - severity → color (warning / exceeded / cap_reached).
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Ban, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useBillingState } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import type { QuotaSeverity } from "@/types/api";

/** Roles that can trigger the upgrade flow (`billing.subscribe`). */
const UPGRADE_ROLES = ["owner", "admin"] as const;

/** Settings landing page hosts the Billing tab (same target as LifecycleBanner). */
const UPGRADE_TARGET = "/settings";

const UPGRADE_DISABLED_TITLE = "Only owners and admins can upgrade the plan";

interface SeverityVariant {
  className: string;
  Icon: typeof AlertTriangle;
  testId: string;
  /** Human label for the driving metric, keyed by its plan-limits key. */
  message: (metricLabel: string) => string;
}

/** Plan-limits key → human label for the banner copy. */
const METRIC_LABELS: Record<string, string> = {
  ingest_gb_month: "ingest",
  query_gb_month: "query",
  storage_gb: "storage",
  endpoint_count: "endpoints",
};

const SEVERITY_VARIANTS: Record<QuotaSeverity, SeverityVariant> = {
  warning: {
    className:
      "bg-amber-100 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100 border-b border-amber-300/60 dark:border-amber-700/60",
    Icon: AlertTriangle,
    testId: "quota-banner-warning",
    message: (m) => `Approaching your ${m} quota for this month.`,
  },
  exceeded: {
    className:
      "bg-orange-100 text-orange-950 dark:bg-orange-900/40 dark:text-orange-100 border-b border-orange-300/60 dark:border-orange-700/60",
    Icon: TrendingUp,
    testId: "quota-banner-exceeded",
    message: (m) => `You have exceeded your ${m} quota — overage charges apply.`,
  },
  cap_reached: {
    className:
      "bg-destructive text-destructive-foreground border-b border-destructive/80",
    Icon: Ban,
    testId: "quota-banner-cap-reached",
    message: (m) => `Your ${m} quota is reached — new data is being blocked.`,
  },
};

export function QuotaBanner({ basePath = "/app" }: { basePath?: string }) {
  const { user } = useAuth();
  const { data, isError, isPending } = useBillingState();

  // First paint and error: render nothing (informational; never block).
  if (isPending || isError || !data) return null;

  const quota = data.quota;
  // Mode gate (defensive — backend returns quota: null outside enforce) +
  // show gate. `quota.mode === "enforce"` is always true when quota is
  // non-null, but we check it explicitly per the contract. `quota.banner?.show`
  // is optional-chained because this banner is mounted in the always-on
  // AppShell: a degraded/version-skewed response missing `banner` must suppress
  // the banner, never throw and white-screen the shell.
  if (!quota || quota.mode !== "enforce" || !quota.banner?.show) return null;

  const severity = quota.banner.severity;
  if (severity === null) return null;
  const variant = SEVERITY_VARIANTS[severity];
  if (!variant) return null;

  const metricKey = quota.banner.metric ?? "";
  const metricLabel = METRIC_LABELS[metricKey] ?? "usage";

  const canUpgrade =
    user?.roles?.some((r) => UPGRADE_ROLES.includes(r as (typeof UPGRADE_ROLES)[number])) ?? false;

  const ctaClasses = cn(
    buttonVariants({ size: "sm", variant: "default" }),
    !canUpgrade && "cursor-not-allowed opacity-50",
  );

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={variant.testId}
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-2 text-sm",
        variant.className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <variant.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate font-medium">{variant.message(metricLabel)}</span>
      </div>
      {canUpgrade ? (
        <Link to={`${basePath}${UPGRADE_TARGET}`} className={ctaClasses}>
          Upgrade
        </Link>
      ) : (
        <button
          type="button"
          disabled
          aria-disabled="true"
          title={UPGRADE_DISABLED_TITLE}
          className={ctaClasses}
        >
          Upgrade
        </button>
      )}
    </div>
  );
}
